import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, fmt, type PortalSettingsDto, type PortalTeaserDto, type PublicEventDto } from '../api';

export default function Home() {
  const [events, setEvents] = useState<PublicEventDto[]>([]);
  const [teasers, setTeasers] = useState<PortalTeaserDto[]>([]);
  const [portalSettings, setPortalSettings] = useState<PortalSettingsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'early-bird'>('all');
  const [carouselIndex, setCarouselIndex] = useState(0);

  // Lead modal state
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadName, setLeadName] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSuccess, setLeadSuccess] = useState(false);
  const [leadError, setLeadError] = useState('');

  useEffect(() => {
    document.title = 'JamTick — ศูนย์รวมตั๋วคอนเสิร์ตและเฟสติวัลสุดฮิต';
    Promise.all([
      api.getPublicEvents(),
      api.getPortalSettings().catch(() => null),
      // Forthcoming events are an extra, not a dependency: if this route is unavailable the portal
      // still lists everything that is actually on sale.
      api.getPortalTeasers().catch(() => [] as PortalTeaserDto[]),
    ])
      .then(([data, settings, upcoming]) => {
        const sorted = [...data].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        setEvents(sorted);
        setTeasers(upcoming);
        if (settings) setPortalSettings(settings);
        setLoading(false);
      })
      .catch((err) => {
        console.error('[home] failed to fetch events', err);
        setError('ไม่สามารถโหลดข้อมูลอีเวนต์ได้');
        setLoading(false);
      });
  }, []);

  const handleLeadSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!leadName.trim() || !leadPhone.trim()) {
      setLeadError('กรุณากรอกชื่อและเบอร์โทรศัพท์');
      return;
    }
    setLeadSubmitting(true);
    setLeadError('');
    try {
      await api.submitLead(leadName, leadPhone);
      setLeadSuccess(true);
    } catch {
      setLeadError('ไม่สามารถส่งข้อมูลได้ กรุณาลองใหม่อีกครั้ง');
    } finally {
      setLeadSubmitting(false);
    }
  };

  // Filter events by search query and category
  const filteredEvents = events.filter((e) => {
    const matchSearch =
      e.name.toLowerCase().includes(search.toLowerCase()) ||
      e.tagline.toLowerCase().includes(search.toLowerCase()) ||
      e.slug.toLowerCase().includes(search.toLowerCase());
    if (!matchSearch) return false;
    if (filter === 'active') return e.status === 'active';
    if (filter === 'early-bird') return e.phase === 'early-bird';
    return true;
  });

  /*
   * TICK-074 — the hero carousel runs over both lists, as a tagged union rather than a widened
   * event.
   *
   * The whole reason the teasers arrive on their own route is that a merged list is one field away
   * from putting a buy button on something that is not for sale. Merging them here would give that
   * back if the merge produced one shape: a teaser has no `slug` and no `minPrice`, so a single
   * type would have to make both optional, and "optional" is exactly what a `{ev.minPrice > 0 &&}`
   * silently accepts.
   *
   * Tagging instead keeps the distinction the payloads make. The renderer reaches `slug` only in
   * the `event` branch — where the type has one — so a slide that cannot be bought cannot grow a
   * link to a page that 404s.
   *
   * Selling events lead: a card someone can act on should not sit behind one they cannot.
   */
  const carouselSlides: ({ kind: 'event'; ev: PublicEventDto } | { kind: 'teaser'; ev: PortalTeaserDto })[] = [
    ...events
      .filter((e) => e.status !== 'archived' && e.carouselVisible !== false)
      .map((ev) => ({ kind: 'event' as const, ev })),
    ...teasers
      .filter((t) => t.carouselVisible !== false)
      .map((ev) => ({ kind: 'teaser' as const, ev })),
  ];

  // Auto rotate carousel every 5s
  useEffect(() => {
    if (carouselSlides.length <= 1) return;
    const interval = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % carouselSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [carouselSlides.length]);

  return (
    <div className="page home-portal">
      {/* Header */}
      <header className="site-header shell sticky">
        <div className="container portal-home-header">
          <Link to="/" className="logo portal-home-logo" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>JAMTICK</span>
            <span className="mono-tag portal-home-portal-tag" style={{ fontSize: 10, padding: '3px 8px', borderColor: '#ff9a3d', color: '#ff9a3d' }}>
              PORTAL
            </span>
          </Link>
          <div className="portal-home-actions">
            <div className="portal-home-live-badge">
              <span className="portal-live-dot" />
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, color: '#f87171', letterSpacing: '0.06em' }}>
                {events.filter((e) => e.status === 'active').length}{' '}
                <span className="portal-home-label-full">EVENTS LIVE NOW</span>
                <span className="portal-home-label-short">LIVE</span>
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="shell container" style={{ paddingTop: 32, paddingBottom: 64 }}>
        {loading ? (
          <div style={{ padding: '80px 0', textAlign: 'center', color: 'var(--muted)' }}>
            <div className="pulse" style={{ fontSize: 16 }}>กำลังโหลดรายการอีเวนต์...</div>
          </div>
        ) : error ? (
          <div className="notice-bar error" style={{ borderRadius: 8, margin: '40px 0' }}>
            {error}
          </div>
        ) : (
          <>
            {/* Hero Carousel */}
            {carouselSlides.length > 0 && (
              <section className="portal-carousel-wrap">
                <div className="portal-carousel">
                  {carouselSlides.map((slide, idx) => {
                    const ev = slide.ev;
                    const isActive = idx === carouselIndex;
                    return (
                      <div
                        key={`${slide.kind}-${ev.id}`}
                        className={`portal-slide ${isActive ? 'active' : ''}`}
                        style={{
                          backgroundImage: ev.heroImageUrl
                            ? `linear-gradient(to top, rgba(23, 23, 23, 0.95) 10%, rgba(23, 23, 23, 0.4) 60%, rgba(23, 23, 23, 0.7) 100%), url(${ev.heroImageUrl})`
                            : `linear-gradient(135deg, #2a2a2a 0%, #171717 100%)`,
                        }}
                      >
                        <div className="portal-slide-content">
                          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
                            {/* The selling tag blinks; a forthcoming one must not borrow that urgency. */}
                            {slide.kind === 'event' ? (
                              <span className="mono-tag early-bird-tag">{slide.ev.phaseLabel}</span>
                            ) : (
                              <span className="mono-tag" style={{ color: '#8b8b8b', borderColor: '#8b8b8b' }}>เร็ว ๆ นี้</span>
                            )}
                            {ev.dateLabel && <span className="mono-label" style={{ color: 'var(--fg)' }}>{ev.dateLabel}</span>}
                          </div>
                          <h1 className="portal-slide-title">{ev.name}</h1>
                          {ev.tagline && <p className="portal-slide-tagline">{ev.tagline}</p>}
                          <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 24, flexWrap: 'wrap' }}>
                            {slide.kind === 'event' ? (
                              <>
                                <Link to={`/e/${slide.ev.slug}`} className="portal-btn-primary">
                                  ซื้อบัตรเข้างาน
                                </Link>
                                {slide.ev.minPrice > 0 && (
                                  <div style={{ fontSize: 14, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
                                    ราคาเริ่มต้น <strong style={{ color: 'var(--fg)', fontSize: 18 }}>{fmt(slide.ev.minPrice)}</strong>
                                  </div>
                                )}
                              </>
                            ) : (
                              /*
                               * No button at all, rather than a disabled one: the event 404s on
                               * every public route, so there is nothing behind a control here for
                               * it to lead to — and a greyed-out button invites the click that
                               * finds that out.
                               */
                              <div style={{ fontSize: 14, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
                                ยังไม่เปิดขาย · รอประกาศวันเปิดจำหน่าย
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Controls */}
                  {carouselSlides.length > 1 && (
                    <>
                      <button
                        type="button"
                        className="portal-carousel-ctrl prev"
                        onClick={() => setCarouselIndex((prev) => (prev - 1 + carouselSlides.length) % carouselSlides.length)}
                        aria-label="Previous slide"
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        className="portal-carousel-ctrl next"
                        onClick={() => setCarouselIndex((prev) => (prev + 1) % carouselSlides.length)}
                        aria-label="Next slide"
                      >
                        ›
                      </button>
                      <div className="portal-carousel-dots">
                        {carouselSlides.map((_, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className={`portal-dot ${idx === carouselIndex ? 'active' : ''}`}
                            onClick={() => setCarouselIndex(idx)}
                            aria-label={`Go to slide ${idx + 1}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </section>
            )}

            {/*
              * Two different silences, and only one of them deserves a box.
              *
              * "ไม่พบรายการอีเวนต์ตรงตามเงื่อนไข" answers a search or a filter that matched nothing —
              * it tells the reader their own input is why the grid is empty. When the portal has no
              * sellable event at all it answers a question nobody asked, on a page that may be
              * showing a hero and a forthcoming card at the same time, which makes it read as a
              * fault rather than a state.
              *
              * So the whole block goes — heading, search, filter pills and grid — because there is
              * nothing to search and nothing to filter. `events`, not `filteredEvents`: the moment
              * one sellable event exists the controls come back, empty result and all.
              */}
            {events.length > 0 && (
            <>
            {/* Filter & Search Bar */}
            <section id="events" style={{ marginTop: 48, marginBottom: 28 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
                <div>
                  <h2 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>อีเวนต์ทั้งหมด</h2>
                  <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>เลือกซื้อบัตรคอนเสิร์ตและเฟสติวัลที่คุณสนใจ</div>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Search Input */}
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      placeholder="ค้นหาชื่ออีเวนต์..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{
                        background: 'var(--panel)',
                        border: '1px solid var(--border)',
                        color: 'var(--fg)',
                        borderRadius: 8,
                        padding: '9px 14px',
                        fontSize: 13,
                        width: 220,
                      }}
                    />
                  </div>

                  {/* Filter Pills */}
                  <div style={{ display: 'flex', gap: 6, background: 'var(--panel)', padding: 4, borderRadius: 8, border: '1px solid var(--border)' }}>
                    <button
                      type="button"
                      className={`portal-filter-pill ${filter === 'all' ? 'active' : ''}`}
                      onClick={() => setFilter('all')}
                    >
                      ทั้งหมด
                    </button>
                    <button
                      type="button"
                      className={`portal-filter-pill ${filter === 'active' ? 'active' : ''}`}
                      onClick={() => setFilter('active')}
                    >
                      กำลังเปิดขาย
                    </button>
                    <button
                      type="button"
                      className={`portal-filter-pill ${filter === 'early-bird' ? 'active' : ''}`}
                      onClick={() => setFilter('early-bird')}
                    >
                      Early Bird
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* Events Grid */}
            <section>
              {filteredEvents.length === 0 ? (
                <div style={{ padding: '60px 20px', textAlign: 'center', background: 'var(--panel)', borderRadius: 12, border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 16, color: 'var(--muted)' }}>ไม่พบรายการอีเวนต์ตรงตามเงื่อนไข</div>
                </div>
              ) : (
                <div className="portal-grid">
                  {filteredEvents.map((ev) => (
                    <Link key={ev.id} to={`/e/${ev.slug}`} className="portal-card">
                      <div className="portal-card-thumb">
                        {ev.heroImageUrl ? (
                          <img src={ev.heroImageUrl} alt={ev.name} loading="lazy" />
                        ) : (
                          <div className="portal-card-placeholder">JAMTICK</div>
                        )}
                        <span className="portal-card-chip">
                          {ev.phase === 'early-bird' ? 'EARLY BIRD' : 'REGULAR'}
                        </span>
                      </div>
                      <div className="portal-card-body">
                        <div style={{ fontSize: 12, color: '#ff9a3d', fontFamily: 'var(--mono)', fontWeight: 600, marginBottom: 4 }}>
                          {ev.dateLabel || 'เร็วๆ นี้'}
                        </div>
                        <h3 className="portal-card-title">{ev.name}</h3>
                        {ev.tagline && <p className="portal-card-desc">{ev.tagline}</p>}
                        <div className="portal-card-foot">
                          <div style={{ fontSize: 13, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
                            เริ่มต้น <strong style={{ color: 'var(--fg)', fontSize: 16 }}>{fmt(ev.minPrice)}</strong>
                          </div>
                          <span className="portal-card-btn">ซื้อบัตร →</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>
            </>
            )}

            {/*
              * TICK-074 — forthcoming events.
              *
              * Rendered as `<div>`, never `<Link>`. The events behind these cards are still in UAT
              * and answer 404 on every public route, so a link would be broken for exactly the
              * people being shown it. No price and no buy button either: nothing here is on sale.
              */}
            {teasers.length > 0 && (
              <section style={{ marginTop: 48 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                  <span className="mono-tag" style={{ borderColor: '#8b8b8b', color: '#8b8b8b', fontSize: 11, padding: '4px 12px' }}>
                    COMING SOON
                  </span>
                  <h2 style={{ fontSize: 20, margin: 0 }}>พบกันเร็ว ๆ นี้</h2>
                </div>
                <div className="portal-grid">
                  {teasers.map((ev) => (
                    <div key={ev.id} className="portal-card" style={{ cursor: 'default' }}>
                      <div className="portal-card-thumb">
                        {ev.heroImageUrl ? (
                          <img src={ev.heroImageUrl} alt={ev.name} loading="lazy" />
                        ) : (
                          <div className="portal-card-placeholder">JAMTICK</div>
                        )}
                        <span className="portal-card-chip" style={{ background: 'rgba(0,0,0,0.6)' }}>
                          เร็ว ๆ นี้
                        </span>
                      </div>
                      <div className="portal-card-body">
                        <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)', fontWeight: 600, marginBottom: 4 }}>
                          {ev.dateLabel || 'ประกาศวันเร็ว ๆ นี้'}
                        </div>
                        <h3 className="portal-card-title">{ev.name}</h3>
                        {ev.tagline && <p className="portal-card-desc">{ev.tagline}</p>}
                        <div className="portal-card-foot">
                          <div style={{ fontSize: 13, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
                            ยังไม่เปิดขาย
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* For Organizer Section */}
            {(!portalSettings || portalSettings.organizerSectionEnabled !== false) && (
              <section className="portal-organizer-box" id="organizers">
                <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
                  <span className="mono-tag" style={{ borderColor: '#ff9a3d', color: '#ff9a3d', fontSize: 11, padding: '4px 12px', marginBottom: 16, display: 'inline-block' }}>
                    JAMTICK FOR ORGANIZERS
                  </span>
                  <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 800, color: 'var(--fg)', margin: '8px 0 16px 0', lineHeight: 1.25 }}>
                    {portalSettings?.organizerSectionTitle || 'สร้างระบบขายตั๋วคอนเสิร์ตและเฟสติวัลของคุณเอง'}
                  </h2>
                  <p style={{ color: 'var(--muted)', fontSize: 15, lineHeight: 1.6, margin: '0 0 36px 0' }}>
                    {portalSettings?.organizerSectionSubtitle || 'แพลตฟอร์มจัดการตั๋วแบบ Multi-tenant แยก Workspace อิสระ รองรับระบบตรวจสอบสลิปอัตโนมัติ และสแกน QR หน้างานเรียลไทม์'}
                  </p>

                  <div className="portal-organizer-grid">
                    {(portalSettings?.organizerCards || [
                      { emoji: '🚀', title: 'Workspace อิสระ', desc: 'รับ URL ประจำงาน `/e/your-event` มีระบบแอดมินและตั๋วจำหน่ายแยกเฉพาะงาน' },
                      { emoji: '⚡', title: 'ตรวจสลิปอัตโนมัติ', desc: 'เชื่อมต่อ EasySlip / Thunder Verify ตรวจสอบยอดและสลิปปลอมอัตโนมัติ 24 ชม.' },
                      { emoji: '📱', title: 'สแกน QR หน้างาน', desc: 'ทีมงานสแกนบัตรผ่านกล้องสมาร์ตโฟน เช็กอินรวดเร็ว ป้องกันตั๋วซ้ำเรียลไทม์' },
                      { emoji: '📊', title: 'Dashboard รายงานผล', desc: 'ติดตามยอดขาย รายรับ โควต้าตั๋ว และส่งออกรายงาน CSV บัญชีได้ทันที' },
                    ]).map((card, cIdx) => (
                      <div className="portal-organizer-card" key={cIdx}>
                        <div style={{ fontSize: 24, marginBottom: 10 }}>{card.emoji}</div>
                        <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 6px 0' }}>{card.title}</h3>
                        <p style={{ fontSize: 12.5, color: 'var(--muted)', margin: 0, lineHeight: 1.5 }}>
                          {card.desc}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/*
                    TICK-094 — the guide sits beside the contact button rather than in the footer.
                    An organizer weighing the platform is the reader it was written for, and this is
                    the one place on the portal where that person is already standing.
                  */}
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={() => { setShowLeadModal(true); setLeadSuccess(false); setLeadError(''); }}
                      className="portal-btn-primary"
                      style={{ border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, padding: '15px 36px', fontSize: 16, borderRadius: 12 }}
                    >
                      ✉️ ติดต่อทีมงาน JamTick
                    </button>
                    <Link
                      to="/guide/ticket-delivery"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8, padding: '15px 28px',
                        fontSize: 15, borderRadius: 12, border: '1px solid var(--border)',
                        color: 'var(--fg)', textDecoration: 'none',
                      }}
                    >
                      📘 คู่มือการส่งมอบบัตร
                    </Link>
                  </div>
                </div>
              </section>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer style={{ borderTop: '1px solid var(--line)', background: 'var(--panel)', padding: '36px 0 48px' }}>
        <div className="shell container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontWeight: 700, letterSpacing: '0.2em', fontSize: 14, color: 'var(--fg)' }}>JAMTICK PORTAL</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>ระบบจำหน่ายตั๋วคอนเสิร์ตและอีเวนต์แบบครบวงจร</div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
            © 2026 JamTick. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Contact Lead Modal */}
      {showLeadModal && (
        <div className="sa-modal-overlay" onClick={() => setShowLeadModal(false)}>
          <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sa-modal-head">
              <div>
                <div className="title">ติดต่อทีมงาน JamTick</div>
                <div className="sub">กรอกข้อมูลของท่าน ทีมงานจะติดต่อกลับเพื่อแนะนำการสร้างระบบโดยเร็วที่สุด</div>
              </div>
              <button className="sa-icon-btn" onClick={() => setShowLeadModal(false)}>✕</button>
            </div>
            {leadSuccess ? (
              <div className="sa-modal-body" style={{ textAlign: 'center', padding: '36px 20px' }}>
                <div style={{ fontSize: 44, marginBottom: 12 }}>🎉</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 8px 0', color: '#8fca9a' }}>ส่งข้อมูลเรียบร้อยแล้ว!</h3>
                <p style={{ color: 'var(--muted)', fontSize: 14, margin: '0 0 24px 0', lineHeight: 1.5 }}>
                  ขอบคุณที่สนใจ JamTick ทีมงานจะติดต่อกลับไปยังเบอร์ <strong>{leadPhone}</strong> โดยเร็วที่สุด
                </p>
                <button
                  className="btn-primary"
                  onClick={() => {
                    setShowLeadModal(false);
                    setLeadSuccess(false);
                    setLeadName('');
                    setLeadPhone('');
                  }}
                >
                  ตกลง
                </button>
              </div>
            ) : (
              <form onSubmit={handleLeadSubmit}>
                <div className="sa-modal-body">
                  {leadError && <div className="form-error" style={{ marginBottom: 16 }}>{leadError}</div>}
                  <div className="sa-field">
                    <div className="sa-field-label">ชื่อผู้ติดต่อ / ชื่องาน *</div>
                    <input
                      type="text"
                      required
                      value={leadName}
                      onChange={(e) => setLeadName(e.target.value)}
                      placeholder="ระบุชื่อของคุณ หรือชื่องานคอนเสิร์ต/ผู้จัด"
                    />
                  </div>
                  <div className="sa-field">
                    <div className="sa-field-label">เบอร์โทรศัพท์ติดต่อ *</div>
                    <input
                      type="tel"
                      required
                      value={leadPhone}
                      onChange={(e) => setLeadPhone(e.target.value)}
                      placeholder="08X-XXX-XXXX"
                    />
                  </div>
                </div>
                <div className="sa-modal-foot">
                  <button type="button" className="btn-ghost" onClick={() => setShowLeadModal(false)}>ยกเลิก</button>
                  <button type="submit" className="btn-primary" disabled={leadSubmitting}>
                    {leadSubmitting ? 'กำลังส่งข้อมูล...' : 'ส่งข้อมูลให้ทีมงานติดต่อกลับ'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
